import {DateFilterItem, DateFilterItemDefinition, FeatureFilter, getFilterItems, MapEditorActions} from '@mc/react-mapio';
import {Button, Menu, MenuItem, Modal} from '@mc/ui-v4';
import React, {MutableRefObject, useMemo} from 'react';
import './DateFilterSelector.scss';
import {DatePickerModal} from '../modals/DatePickerModal';
import {deriveDateFilterItemFromRange, getDateFilterLabel} from '../../helpers/DateFilters';

type Props = {
    mapRef: MutableRefObject<MapEditorActions>;
    disabled?: boolean;
    dateFilterItems: DateFilterItemDefinition[];
    dateFilterRange: FeatureFilter | null;
    setDateFilterRange: React.Dispatch<React.SetStateAction<FeatureFilter | null>>;
};

/**
 * Komponenta DateFilterSelector.
 * @param mapRef - Reference na akce editoru mapy.
 * @param disabled - Určuje, zda je komponenta deaktivovaná.
 * @param dateFilterItems - Dostupné položky filtru podle data.
 * @param dateFilterRange - Aktuální rozsah filtru podle data.
 * @param setDateFilterRange - Funkce pro nastavení rozsahu filtru podle data.
 */
export const DateFilterSelector: React.FC<Props> = ({mapRef, disabled, dateFilterItems, dateFilterRange, setDateFilterRange}) => {
    // Odvozujeme aktivní DateFilterItem z dateFilterRange
    const dateFilter = useMemo(() => deriveDateFilterItemFromRange(dateFilterRange, dateFilterItems), [dateFilterRange, dateFilterItems]);
    // Vyfiltrujeme si jen ty možnosti, které jsou v dateFilterItems
    // a každé možnosti přiřadíme aktuální value, krom CUSTOM_DATE a CUSTOM_RANGE,
    // protože ty mají vlastní modal s kalendáře
    const selectItems = useMemo(() => getFilterItems(dateFilterItems), [dateFilterItems]);

    return (
        <div className="date_filter_select">
            <Button
                style="link"
                dropdown
                disabled={disabled}
                menu={
                    <Menu>
                        {selectItems.map(item => {
                            // Type guard - item je vždy objekt s id, name a value
                            const itemId = typeof item === 'string' ? item : item.id;
                            const itemName = typeof item === 'string' ? item : item.name;
                            const itemValue = typeof item === 'object' && 'value' in item ? item.value : null;

                            return (
                                <MenuItem
                                    key={itemId}
                                    text={itemName}
                                    active={itemId === dateFilter}
                                    onClick={() => {
                                        if (itemId === DateFilterItem.CUSTOM_RANGE) {
                                            Modal.show(({onClose}) => (
                                                <DatePickerModal
                                                    onClose={onClose}
                                                    initialValues={dateFilterRange}
                                                    onSubmit={async data => {
                                                        setDateFilterRange(data);
                                                        mapRef.current?.setFilter(data);
                                                        onClose();
                                                    }}
                                                    dateRange={true}
                                                    title={'Vyberte vlastní odobí'}
                                                />
                                            ));
                                        } else if (itemId === DateFilterItem.CUSTOM_DATE) {
                                            Modal.show(({onClose}) => (
                                                <DatePickerModal
                                                    onClose={onClose}
                                                    initialValues={dateFilterRange}
                                                    onSubmit={async data => {
                                                        setDateFilterRange(data);
                                                        mapRef.current?.setFilter(data);
                                                        onClose();
                                                    }}
                                                    title={'Vyberte vlastní den'}
                                                    dateRange={false}
                                                />
                                            ));
                                        } else {
                                            setDateFilterRange(itemValue);
                                            mapRef.current.setFilter(itemValue);
                                        }
                                    }}
                                />
                            );
                        })}
                    </Menu>
                }
            >
                {getDateFilterLabel(dateFilterRange, dateFilterItems)}
            </Button>
        </div>
    );
};
